import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/api";
import { ArrowRight, History, LockKeyhole, ShieldCheck, Sparkles, CheckCircle2 } from "lucide-react";
import { BrandMark } from "./BrandMark";

export const SecuritySection = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60"
        data-testid="security-section"
    >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
                <div className="mono-label mb-3 flex items-center gap-1.5">
                    <LockKeyhole className="h-3 w-3" strokeWidth={1.5} />
                    Security first
                </div>
                <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]">
                    Isolation is not
                    <br />
                    a feature. It&#39;s the default.
                </h3>
                <p className="mt-6 text-muted-foreground max-w-lg">
                    Every client tenant is enforced at the API layer with
                    request-scoped role checks. Passwords are bcrypt-hashed,
                    JWTs are short-lived, and every evidence upload lives in a
                    per-tenant object-storage path.
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                    {
                        icon: LockKeyhole,
                        title: "Zero cross-tenant reads",
                        body: "All /clients/{id}/* endpoints check ownership on every request.",
                    },
                    {
                        icon: ShieldCheck,
                        title: "Bcrypt + JWT",
                        body: "Passwords hashed with a per-user salt; tokens signed with a rotating secret.",
                    },
                    {
                        icon: History,
                        title: "Audit-preserving history",
                        body: "Every PDF export is version-locked in object storage.",
                    },
                    {
                        icon: Sparkles,
                        title: "Sensible defaults",
                        body: "No user enumeration on password reset. 60-min single-use tokens.",
                    },
                ].map((s) => (
                    <div key={s.title} className="flex items-start gap-3 rounded-xl border border-border/70 bg-card p-4">
                        <s.icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0">
                            <div className="font-display font-semibold text-sm">
                                {s.title}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                {s.body}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        <div
            id="security-posture"
            className="mt-10 rounded-2xl border border-border/70 bg-card p-6 sm:p-8 scroll-mt-20"
            data-testid="security-posture-block"
        >
            <div className="mono-label mb-3 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" strokeWidth={1.5} />
                Our own security posture
            </div>
            <h4 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
                Building toward the compliance we help you deliver.
            </h4>
            <p className="mt-3 text-sm text-muted-foreground" data-testid="security-posture-status">
                Aligned to SOC 2 Trust Services Criteria. Independent audit not yet started.
            </p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="security-posture-proofs">
                {[
                    "Tenant isolation enforced at the API layer on every request",
                    "SHA-256 hash-chained, tamper-evident audit log",
                    "Independent penetration test: planned",
                ].map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        <span>{p}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <a
                    href="#request-demo"
                    onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("request-demo")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        trackEvent("landing_cta_click", { location: "security_posture", label: "security_summary" });
                    }}
                    className="inline-flex items-center gap-1.5 font-display text-sm font-semibold underline underline-offset-4 hover:opacity-80 transition-opacity tap-target"
                    data-testid="security-posture-cta"
                >
                    Request our security summary (under NDA)
                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </a>
                <span className="text-xs text-muted-foreground" data-testid="security-posture-updated">
                    Last updated: November 2026
                </span>
            </div>
        </div>
    </section>
);

export const FinalCtaSection = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:pb-24"
        data-testid="final-cta"
    >
        <div
            className="rounded-3xl border border-border p-10 sm:p-16 relative overflow-hidden"
            style={{ backgroundColor: "hsl(var(--card))" }}
        >
            <div className="relative">
                <div className="mono-label mb-4 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" strokeWidth={1.5} />
                    Ready in 60 seconds
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                        Give every client their own workspace.
                    </h2>
                    <div className="flex flex-col lg:items-end gap-3">
                        <Link to="/register" className="w-full">
                            <Button
                                size="lg"
                                className="rounded-md font-display px-6 sm:px-8 w-full h-auto min-h-[52px] py-3 whitespace-normal"
                                data-testid="footer-cta-register"
                                onClick={() =>
                                    trackEvent("landing_cta_click", {
                                        location: "final_cta",
                                        label: "get_started",
                                    })
                                }
                            >
                                Start free
                                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                            </Button>
                        </Link>
                        <span className="text-[13px] text-muted-foreground whitespace-nowrap">
                            No credit card · Demo client included
                        </span>
                        <span className="text-[13px] text-muted-foreground whitespace-nowrap" data-testid="final-cta-integrations">
                            Works with Okta · AWS · GitHub
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

export const LandingFooter = () => (
    <footer className="max-w-7xl mx-auto px-5 sm:px-10 py-12 border-t border-border/60">
        <BrandMark />
        <nav className="mt-8 grid grid-cols-2 gap-x-10 gap-y-1 max-w-xs" aria-label="Footer">
            {[
                { to: "/pricing", label: "Pricing", tid: "footer-pricing" },
                { to: "/about", label: "Team", tid: "footer-about" },
                { to: "/security", label: "Security", tid: "footer-security" },
                { to: "/#security-posture", label: "Security posture", tid: "footer-security-posture" },
                { to: "/changelog", label: "Changelog", tid: "footer-changelog" },
                { to: "/contact", label: "Contact", tid: "footer-contact" },
                { to: "/privacy", label: "Privacy Policy", tid: "footer-privacy" },
                { to: "/login", label: "Sign in", tid: "footer-signin" },
            ].map((l) => (
                <Link
                    key={l.to}
                    to={l.to}
                    className="inline-flex items-center text-[15px] text-muted-foreground hover:text-foreground transition-colors py-3"
                    data-testid={l.tid}
                >
                    {l.label}
                </Link>
            ))}
        </nav>
        <p className="mt-8 text-[13px] text-muted-foreground" data-testid="footer-attribution">
            © {new Date().getFullYear()} AuditVanguard · GSGD Field Solutions LLC
        </p>
    </footer>
);
